import { useForm } from 'react-hook-form';
import { useModalStoreState, useWithdrawOperationData } from '@/shared/lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createWithdrawBankTransferSchema,
  type CurrencyOption,
  type FormStep,
  useCommissionData,
  useFormSteps,
  useGetWithdrawParams,
  type WithdrawAmountValues,
  type WithdrawBankTransferFormValues,
  type WithdrawBankTransferState,
} from '@/features/withdraw-bank-transfer';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  type FiatWithdrawAssetOption,
  getFiatWithdrawAssetOptions,
  useWithdrawTotalAmount,
} from '@/entities/operation';
import { getMinAmount } from '@/features/withdraw-bank-transfer/lib/getMinAmount.ts';
import { useGetSavedBankAddresses } from '@/entities/bank-address';

interface UseWithdrawCardFormProps {
  onValidateSuccess: () => void;
}

export function useWithdrawBankTransferForm({
  onValidateSuccess,
}: UseWithdrawCardFormProps) {
  const { t } = useTranslation();
  const isMountedRef = useRef(false);
  const { setMultipleState, getAllState } =
    useModalStoreState<WithdrawBankTransferState>();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const {
    withdrawBankTransferFormData = {},
    formStepsData = [],
    currentStepIndex = 0,
    lastSubmittedStepIndex = 0,
  } = getAllState();

  const {
    data: savedBankAddresses,
    isLoading: isSavedBankAddressesLoading,
    error: savedBankAddressesError,
  } = useGetSavedBankAddresses();

  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyOption | undefined
  >();
  const [templateId, setTemplateId] = useState<string | undefined>();
  const { paramsData, isParamsDataLoading, paramsDataError } =
    useGetWithdrawParams();

  const {
    ewallet,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
    depositWithdrawAssetsIsError,
    depositWithdrawAssetsData,
    assetsIconsData,
    assetsData,
    mainAccount,
  } = useWithdrawOperationData();

  const assetOptions = useMemo<FiatWithdrawAssetOption[]>(() => {
    return getFiatWithdrawAssetOptions({
      key: 'bank',
      assetsData,
      assetsIconsData,
      depositWithdrawAssetsData,
      mainAccount,
      ewallet,
    });
  }, [
    assetsData,
    assetsIconsData,
    ewallet,
    depositWithdrawAssetsData,
    mainAccount,
  ]);

  const validationSchema = useMemo(() => {
    const currentStep = formStepsData[currentStepIndex];
    const params = currentStep?.params || paramsData?.params || [];
    const minAmount = getMinAmount(currentStep, selectedCurrency);

    return createWithdrawBankTransferSchema(
      t,
      params,
      !!currentStep?.isDisplayAmount,
      selectedCurrency?.available || 0,
      minAmount,
      totalAmount,
      selectedCurrency?.max,
      t('common.maxAmount', {
        amount: selectedCurrency?.max,
        currency: selectedCurrency?.code ?? '',
      })
    );
  }, [
    selectedCurrency?.max,
    selectedCurrency?.code,
    formStepsData,
    currentStepIndex,
    t,
    selectedCurrency?.available,
    totalAmount,
  ]);

  const form = useForm<WithdrawBankTransferFormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      templateId: '',
    },
  });

  const watchedValues = form.watch();

  useEffect(() => {
    const currencyItem = assetOptions.find(
      a => a.code === watchedValues.currencyCode
    );

    if (currencyItem) {
      setSelectedCurrency(currencyItem);
    }
  }, [watchedValues.currencyCode]);

  const {
    currentStep,
    handleFormStepBack,
    handleFormSubmit,
    isParamsLoading,
    paramsError,
    isFinalStep,
    getCurrentStepData,
  } = useFormSteps({
    form,
    templateId,
    formStepsData,
    currentStepIndex,
    lastSubmittedStepIndex,
    onOpenConfirmationModal: () => {
      setMultipleState({
        accountNumber: mainAccount?.accNum || '',
        feeAmount: commissionData?.feeAmount || 0,
        totalAmount: totalAmount,
        isNeedToSaveTemplate: !templateId,
      });

      onValidateSuccess();
    },
  });

  const { commissionData, commissionError, isGettingCommission } =
    useCommissionData({
      hasAmountError: !!form.formState.errors.amount,
      selectedCurrency,
      amountCurrency: currentStep?.amountCurrency || '',
      amount: Number(watchedValues.amount),
      countryCode: watchedValues.bankCountry as string,
      onTop: watchedValues.includeFee,
    });

  useEffect(() => {
    if (watchedValues.amount) {
      form.clearErrors('amount');
      form.trigger('amount');
    }
  }, [watchedValues.bankCountry]);

  useEffect(() => {
    form.setError('amount', {
      type: 'server',
    });
  }, [commissionError]);

  const handleMaxClick = () => {
    if (selectedCurrency) {
      form.setValue('amount', String(selectedCurrency.available));
    }
  };

  useEffect(() => {
    const formValues = form.getValues();
    const hasAnyFieldFilled = Object.values(formValues).some(value => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      return value;
    });

    setIsFormDirty(hasAnyFieldFilled);
  }, [watchedValues]);

  useEffect(() => {
    if (!paramsData?.params) return;

    if (formStepsData.length === 0) {
      const newStep: FormStep = {
        params: paramsData.params,
        values: currentStep ? { ...currentStep.values } : {},
      };

      setMultipleState({ currentStepIndex: 0, formStepsData: [newStep] });
    }
  }, [paramsData]);

  useEffect(() => {
    if (isMountedRef.current) return;
    if (!withdrawBankTransferFormData) return;

    isMountedRef.current = true;

    // setTimeout is used to fill currencyCode field
    // for the first time when the form is mounted, the currencyCode field is not filled
    setTimeout(() => {
      form.reset(withdrawBankTransferFormData);
    }, 0);
  }, [withdrawBankTransferFormData, form]);

  const [amount, includeFee] = form.watch(['amount', 'includeFee']);

  const amountValues: WithdrawAmountValues = {
    amount: String(amount),
    includeFee: (includeFee as boolean | undefined) ?? false,
  };
  const { totalAmount: calculatedTotalAmount } = useWithdrawTotalAmount<
    WithdrawBankTransferFormValues,
    WithdrawAmountValues
  >({
    form,
    watchedValues: amountValues,
    commissionData,
    shouldTriggerValidation: values =>
      !!currentStep?.isDisplayAmount && !!values.amount,
    isCommissionLoading: isGettingCommission,
    additionalDependencies: [selectedCurrency?.code, currentStep],
    validationDependencies: [
      amountValues.amount,
      currentStep?.isDisplayAmount,
      selectedCurrency?.code,
      totalAmount,
    ],
  });

  useEffect(() => {
    setTotalAmount(calculatedTotalAmount);
  }, [calculatedTotalAmount]);

  return {
    form,
    watchedValues,
    assetOptions,
    savedBankAddresses,
    selectedCurrency,
    setTemplateId,
    hasWithdrawFee: commissionData ? commissionData.feeAmount > 0 : false,
    isFromDataLoading:
      depositWithdrawAssetsLoading ||
      isParamsDataLoading ||
      isSavedBankAddressesLoading,
    isFormDataError: depositWithdrawAssetsIsError || savedBankAddressesError,
    formDataError: depositWithdrawAssetsError,
    isFormDirty,
    commissionData,
    totalAmount,
    handleFormSubmit,
    error: paramsDataError || paramsError || commissionError,
    handleMaxClick,
    isLoading: isGettingCommission,
    accountNumber: mainAccount?.accNum,
    paramsData,
    formStepsData,
    currentStepIndex,
    handleFormStepBack,
    isSubmittingStep: isParamsLoading,
    isFinalStep,
    getCurrentStepData,
  };
}
